// Put the steps in order. The steps are cards in the order the server sent them (already shuffled); each tap gives
// the next number to the card tapped, tapping the card with the last number takes it back, and Lock in sends
// { order } once every card has a number. At the reveal: the right order, with where this player put each step
// beside it, matches ticked.
import { useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Stem } from '@/components/Stem';
import { Button } from '@/components/Button';
import { chem } from '@/clinical/chem';
import { sfx } from '@/audio/sfx';
import { haptic } from '@/lib/haptics';
import type { FormatViewProps } from './types';
import { phaseFor } from './layout';
import { useAnswerSubmit, TickGlyph, CrossGlyph } from './parts';
import { isComplete, positionOf, tapStep, undoStep } from './orderPick';
import { creditFor, type Credit } from './credit';
import { CreditSplit } from './reveal';
import { useNearing } from './useServerNow';
import { Split, useWide } from './split';

type Step = { id: string; text: string };

/** The steps to order: the order spec's, else the options (an item built before the spec existed). */
export function stepsOf(q: FormatViewProps['question']): Step[] {
  const s = q.order?.steps;
  return Array.isArray(s) && s.length ? s : q.options;
}

export function OrderView(props: FormatViewProps) {
  const { question: q, myAnswer, reveal, display = 'phones' } = props;
  const steps = useMemo(() => stepsOf(q), [q]);
  const mine = Array.isArray(myAnswer?.order) && myAnswer.order.length ? myAnswer.order : undefined;
  const phase = phaseFor(props.phase, Boolean(mine), reveal);
  const { submit, live } = useAnswerSubmit({ ...props, phase });
  const [seq, setSeq] = useState<string[]>([]);
  const screen = display === 'screen';
  const revealed = phase === 'revealed' && reveal ? reveal : undefined;
  const credit = revealed && mine ? creditFor(q, revealed, { order: mine }) : undefined;
  const late = useNearing(q.deadlineAt, 5000, live && seq.length > 0);

  const onTap = (id: string) => {
    if (!live) return;
    const r = tapStep(seq, id);
    if (r.action === 'none') { haptic('tap'); return; }
    setSeq(r.seq);
    if (r.action === 'add') sfx.play('pop');
    haptic('tap');
  };

  // On a desktop question the steps and the lock-in bar sit on the right, the bar in the column's flow (not docked).
  const wide = useWide(display);
  return (
    <div className="fx-format" data-format="order" data-phase={phase} data-display={display} data-layout={wide ? 'split' : undefined}>
      <Split wide={wide} left={<>
      {!screen && <Stem text={q.stem} />}
      {screen && <p className="sr-only">The question is on the big screen. Answer here.</p>}
      </>} right={revealed && Array.isArray(revealed.correctOrder) && revealed.correctOrder.length ? (
        <OrderResult steps={steps} correct={revealed.correctOrder} mine={mine} credit={credit} split={revealed.credit} compact={screen} />
      ) : (
        <>
          <StepCards steps={steps} seq={mine ?? seq} live={live} locked={Boolean(mine)} compact={screen} onTap={onTap} />
          {!mine && phase !== 'revealed' && (
            <div className="fx-dock" data-docked={wide ? undefined : ''}>
              {/* Progress only: the stem and the intro card have said what to do, and Undo is beside Lock in. */}
              <p className="fx-dock-note" aria-live="polite" data-late={late ? '' : undefined}>
                {late ? 'Lock in before the buzzer.' : seq.length ? `${seq.length} of ${steps.length} placed.` : ''}
              </p>
              <div className="fx-dock-row">
                <Button variant="quiet" size="lg" className="w-auto" disabled={!live || !seq.length} onClick={() => { setSeq((s) => undoStep(s)); haptic('tap'); }}>Undo</Button>
                <Button variant="primary" size="lg" className={`flex-1${late ? ' fx-go-late' : ''}`} disabled={!live || !isComplete(seq, steps.map((s) => s.id))} onClick={() => submit({ order: seq })}>Lock in</Button>
              </div>
            </div>
          )}
        </>
      )} />
    </div>
  );
}

function StepCards({ steps, seq, live, locked, compact, onTap }: { steps: Step[]; seq: readonly string[]; live: boolean; locked: boolean; compact: boolean; onTap: (id: string) => void }) {
  const reduce = useReducedMotion() ?? false;
  return (
    <ol className="fx-steps" data-compact={compact ? '' : undefined} aria-label="Steps">
      {steps.map((s) => {
        const n = positionOf(seq, s.id);
        const last = !locked && n !== undefined && n === seq.length;
        const state = locked ? 'locked' : n !== undefined ? 'picked' : 'idle';
        return (
          <li key={s.id}>
            <button type="button" className="fx-step" data-option={s.id} data-state={state} data-last={last ? '' : undefined}
              aria-pressed={n !== undefined} disabled={!live}
              aria-label={`${s.text}. ${n !== undefined ? `Step ${n}${last ? '. Tap again to take it back' : ''}` : 'No number yet'}.`}
              onClick={() => onTap(s.id)}>
              <span className="fx-step-n" aria-hidden="true">
                <AnimatePresence initial={false}>
                  {n !== undefined && (
                    <motion.span key={n} initial={reduce ? { opacity: 0 } : { scale: 0.2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={reduce ? { opacity: 0 } : { scale: 0.2, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 600, damping: 22 }}>{n}</motion.span>
                  )}
                </AnimatePresence>
              </span>
              <span className="fx-step-text">{chem(s.text)}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

const ORDINAL = (n: number) => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`; };

/** The right order with this player's own positions beside it; reusable on the stage without a player. */
export function OrderResult({ steps, correct, mine, credit, split, compact = false }: {
  steps: readonly Step[]; correct: readonly string[]; mine?: readonly string[]; credit?: Credit;
  split?: { full: number; partial: number; none: number }; compact?: boolean;
}) {
  const reduce = useReducedMotion() ?? false;
  const byId = new Map(steps.map((s) => [s.id, s]));
  const matches = mine ? correct.filter((id, i) => mine[i] === id).length : 0;
  const verdict = !mine ? undefined
    : credit === 'full' ? 'Every step in its place.'
    : credit === 'partial' ? 'One swap away from the right order.'
    : `${matches} of ${correct.length} in the right place.`;
  return (
    <section className="fx-order" data-compact={compact ? '' : undefined} aria-label="The right order">
      <div className="fx-order-head" aria-hidden="true">
        <span>The right order</span>
        {mine && <span>You</span>}
      </div>
      <ol className="fx-order-list">
        {correct.map((id, i) => {
          const step = byId.get(id);
          const you = mine ? positionOf(mine, id) : undefined;
          const match = you === i + 1;
          return (
            <motion.li key={id} className="fx-order-row" data-match={mine ? (match ? 'yes' : 'no') : undefined} data-state={mine && match ? 'correct' : undefined}
              initial={reduce ? { opacity: 0 } : { opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.07, type: 'spring', stiffness: 380, damping: 30 }}>
              <span className="fx-order-n" aria-hidden="true">{i + 1}</span>
              <span className="fx-order-text">
                <span className="sr-only">{ORDINAL(i + 1)}: </span>{chem(step?.text ?? id)}
                {mine && <span className="sr-only">{you ? (match ? ' You had it here.' : ` You put it ${ORDINAL(you)}.`) : ' You did not place it.'}</span>}
              </span>
              {mine && (
                <span className="fx-order-you" aria-hidden="true">
                  <b>{you ?? '–'}</b>
                  {match ? <TickGlyph size={14} stroke={3.6} /> : <CrossGlyph size={14} stroke={3.6} />}
                </span>
              )}
            </motion.li>
          );
        })}
      </ol>
      {verdict && <p className="fx-verdict">{verdict}</p>}
      {split && <CreditSplit credit={split} mine={mine ? credit : undefined} />}
    </section>
  );
}
